import { Link } from 'react-router-dom';
import { ArrowRight, Star, Shield, Heart, Leaf, RefreshCw, Users, Award } from 'lucide-react';

const TEAM_IMAGE = "/images/team.jpg";

const About = () => {
  const brandValues = [
    {
      icon: Award,
      title: 'Premium Excellence',
      description: 'Uncompromising quality in every wash, using expert techniques and premium-grade products for flawless results.',
    },
    {
      icon: Users,
      title: 'Customer Convenience',
      description: 'Doorstep service designed around your schedule—no travel, no waiting, no disruption.',
    },
    {
      icon: Shield,
      title: 'Trust & Transparency',
      description: 'Clear pricing, honest communication, and responsible handling of every vehicle.',
    },
    {
      icon: Heart,
      title: 'Care & Responsibility',
      description: 'Every car is treated with precision and respect, as if it were our own.',
    },
    {
      icon: Leaf,
      title: 'Eco-Friendly Approach',
      description: 'Water-efficient methods and environmentally safe products for sustainable car care.',
    },
    {
      icon: RefreshCw,
      title: 'Consistency & Reliability',
      description: 'Standardized service processes ensure the same premium experience every time.',
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-[#1E88E5]/10 rounded-full text-[#1E88E5] font-medium text-sm mb-6">
                About Washora
              </div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#0B2C5D] mb-6">
                Who We Are
              </h1>
              <p className="text-lg text-[#64748B] mb-6 leading-relaxed">
                Washora is a premium doorstep car wash and auto care brand dedicated to redefining modern vehicle maintenance. We bring professional, reliable, and high-quality car care services directly to your home, office, or apartment complex.
              </p>
              <p className="text-lg text-[#64748B] mb-8 leading-relaxed">
                Founded by Dr. Swamy K N, Washora was created to eliminate the hassles of traditional car washing – long queues, wasted time, and inconsistent service. Our trained professionals deliver meticulous care while preserving your car's appearance and value.
              </p>
              <div className="bg-[#F7931E]/10 border-l-4 border-[#F7931E] px-4 py-3 rounded-r-lg">
                <p className="font-heading font-semibold text-[#0B2C5D]">
                  Core Karnataka's First Doorstep Car Wash, beyond the capital.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src={TEAM_IMAGE}
                alt="Washora Professional Team"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-[#F7931E]/10 rounded-full flex items-center justify-center">
                    <Star className="h-8 w-8 text-[#F7931E]" />
                  </div>
                  <div>
                    <p className="font-heading text-3xl font-bold text-[#0B2C5D]">500+</p>
                    <p className="text-[#64748B]">Happy Customers</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why We Exist */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-6">
              Why We Exist
            </h2>
            <p className="text-lg text-[#64748B] leading-relaxed">
              We exist to solve the hassle of traditional car washes. Our mission is to save your precious time while ensuring premium quality care for your vehicle. No more long queues, no more wasted weekends at car wash stations.
            </p>
          </div>
        </div>
      </section>

      {/* Brand Values */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-4">
              Our Brand Values
            </h2>
            <p className="text-[#64748B] max-w-2xl mx-auto">
              These core values guide everything we do at Washora.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {brandValues.map((value, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1"
                data-testid={`brand-value-${index}`}
              >
                <div className="w-14 h-14 bg-[#1E88E5]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#1E88E5] transition-colors">
                  <value.icon className="h-7 w-7 text-[#1E88E5] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading text-xl font-bold text-[#0B2C5D] mb-3">
                  {value.title}
                </h3>
                <p className="text-[#64748B]">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Washora Promise */}
      <section className="py-20 bg-[#0B2C5D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-8">
            The Washora Promise
          </h2>
          <div className="flex flex-wrap justify-center gap-8">
            {['Premium Care', 'Trusted Service', 'Time Saved'].map((item, index) => (
              <div key={index} className="flex items-center">
                <div className="w-3 h-3 bg-[#F7931E] rounded-full mr-3"></div>
                <span className="text-xl text-white font-heading font-semibold">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-6">
            Experience Premium Care Today
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto mb-8">
            Ready to see why customers trust us with their vehicles?
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/booking"
              className="bg-[#F7931E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#E08215] transition-all transform hover:-translate-y-0.5 shadow-lg hover:shadow-orange-500/30 flex items-center justify-center"
              data-testid="about-book-now"
            >
              Book Now <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/ceo-desk"
              className="bg-white text-[#0B2C5D] border-2 border-[#0B2C5D] px-8 py-4 rounded-full font-semibold hover:bg-[#0B2C5D] hover:text-white transition-all flex items-center justify-center"
            >
              Read Founder's Message
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
